import type { AdminKanbanDeal, SalesPipelineStage } from "./use-admin-deals";
import { DealCard } from "./deal-card";

// SPA port of /app/admin/deals/_components/Kanban.tsx. Columns = HubSpot
// pipeline stages (resolved server-side); empty stages stay visible so the
// admin can see the full pipeline shape even when a column has no deals.
// No drag-between-columns — stage is HubSpot-driven.

type Props = {
  stages: SalesPipelineStage[];
  grouped: Record<string, AdminKanbanDeal[]>;
  onSelectDeal: (hubspotDealId: string) => void;
};

export function Kanban({ stages, grouped, onSelectDeal }: Props) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {stages.map((stage) => (
        <Column
          key={stage.id}
          stage={stage}
          deals={grouped[stage.id] ?? []}
          onSelectDeal={onSelectDeal}
        />
      ))}
    </div>
  );
}

function Column({
  stage,
  deals,
  onSelectDeal,
}: {
  stage: SalesPipelineStage;
  deals: AdminKanbanDeal[];
  onSelectDeal: (hubspotDealId: string) => void;
}) {
  // Sum amounts by currency so EUR + USD don't silently add as one number
  // — Bookboost has a handful of USD-denominated deals.
  const byCurrency = deals.reduce<Record<string, number>>((acc, d) => {
    const c = d.deal_currency_code ?? "EUR";
    acc[c] = (acc[c] ?? 0) + (d.deal_amount ?? 0);
    return acc;
  }, {});

  return (
    <div className="flex w-[300px] shrink-0 flex-col rounded-xl border bg-card/40 p-2.5">
      <header className="mb-2 flex items-baseline justify-between px-1.5">
        <div className="flex items-center gap-2">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {stage.label}
          </h3>
          <span className="rounded-md bg-background px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {deals.length}
          </span>
        </div>
        {Object.values(byCurrency).some((v) => v > 0) && (
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {Object.entries(byCurrency)
              .filter(([, v]) => v > 0)
              .map(([c, v]) => formatAmount(v, c))
              .join(" · ")}
          </span>
        )}
      </header>

      <div className="flex flex-col gap-2">
        {deals.map((d) => (
          <DealCard
            key={d.hubspot_deal_id}
            deal={d}
            onSelect={() => onSelectDeal(d.hubspot_deal_id)}
          />
        ))}
        {deals.length === 0 && (
          <div className="rounded-md border border-dashed border-border/60 bg-background/40 px-3 py-6 text-center text-[11px] text-muted-foreground/70">
            No deals
          </div>
        )}
      </div>
    </div>
  );
}

function formatAmount(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("en-IE", { maximumFractionDigits: 0 })} ${currency}`;
  }
}
